import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useLayoutEffect,
  useState,
} from "react";

import { DotsSixVerticalIcon } from "@phosphor-icons/react";
import { Box, Card, Flex, IconButton, Text } from "@radix-ui/themes";

import { WIDGET_DRAG_HANDLE_ATTR } from "./dragHandle";
import { WidgetActionsMenu } from "./WidgetActionsMenu";
import { WidgetBodyErrorBoundary } from "./WidgetBodyErrorBoundary";
import "./widget.css";

export {
  WIDGET_DRAG_HANDLE_ATTR,
  WIDGET_DRAG_HANDLE_SELECTOR,
} from "./dragHandle";
export { WidgetActionsMenu } from "./WidgetActionsMenu";

/**
 * DOM node for the right-hand widget header cluster (status tabs, column
 * picker, …). Children portal into it when present; they render in place
 * when this is `null` (standalone surfaces and the first paint before the
 * callback ref attaches). The slot is an ordinary in-theme element — not an
 * overlay — so theming stays on the root `<Theme>`.
 */
export const WidgetHeaderEndSlotContext = createContext<HTMLElement | null>(
  null,
);

/**
 * DOM node immediately after the kind title. Children portal a muted
 * suffix into it (e.g. Positions' "8 open"); `:empty` hides the gap
 * when nothing is published.
 */
export const WidgetTitleEndSlotContext = createContext<HTMLElement | null>(
  null,
);

export const WidgetOverflowMenuItemsContext = createContext<Dispatch<
  SetStateAction<ReactNode>
> | null>(null);

export const WidgetOverflowMenuItems = ({
  children,
}: {
  children: ReactNode;
}) => {
  const setItems = useContext(WidgetOverflowMenuItemsContext);
  useLayoutEffect(() => {
    if (!setItems) {
      return;
    }
    setItems(children);
  }, [children, setItems]);
  // Clearing belongs on unmount only. Pairing it with the publish effect
  // would null the slot whenever `children` identity changes (drag state,
  // quote ticks), which unmounts a DropdownMenu.Sub mid-gesture.
  useLayoutEffect(() => {
    if (!setItems) {
      return;
    }
    return () => setItems(null);
  }, [setItems]);
  return null;
};

export type WidgetAccentColor =
  | "blue"
  | "green"
  | "yellow"
  | "orange"
  | "red"
  | "purple"
  | "pink"
  | "teal";

/** Edge of the handle the group line sits on, or `off` to suppress it. */
export type WidgetGroupLinePosition = "top" | "bottom" | "off";

export interface WidgetProps {
  id: string;
  /** Human-readable header title (kind label, optionally with symbol). */
  title: string;
  /**
   * Ticker control rendered after the kind label (`Chart · {ticker}`).
   * Omit for Positions, Options Chain, and ungrouped widgets that still pass a
   * joined title.
   */
  titleSymbol?: ReactNode;
  /** Optional body content (e.g. a chart). Defaults to a blank placeholder. */
  children?: ReactNode;
  /** Rendered left of the title, next to the drag handle (e.g. the group selector). */
  headerAccessory?: ReactNode;
  /**
   * Radix color identity for the widget's group. Published as
   * `data-accent-color` on the group line, which is Radix's own hook for
   * remapping `--accent-*` onto that scale — so the line tracks light/dark
   * without a manual appearance branch. Absent means "no group", and no line
   * is drawn.
   */
  accentColor?: WidgetAccentColor;
  /** Defaults to `top`; a user-facing setting will drive this later. */
  groupLinePosition?: WidgetGroupLinePosition;
  onRemove?: () => void;
  /** Forwarded to `WidgetBodyErrorBoundary` — called when the body recovers from an error. */
  onReset?: () => void;
}

/**
 * Radix-native shell for every workspace widget. The outer surface is a
 * Radix `Card` — the primitive owns border, background, radius, and the
 * light/dark appearance switch. Layout comes from Radix props; the handful of
 * declarations Themes has no prop for (the card's geometry and zeroed
 * padding, the header hairline, the group line) live in `widget.css`, keyed
 * off `.workspace-widget`.
 *
 * Header clusters match the ATD prototype: drag + group + title on the left,
 * optional body-owned chrome (status tabs, columns) and the actions menu on
 * the right. The body is wrapped in `WidgetBodyErrorBoundary` so a throwing
 * child is isolated to the body region — the header stays mounted. The
 * workspace is always movable and resizable, so the drag handle renders
 * unconditionally and every widget affordance stays interactive at rest; the
 * grid engines gate actual dragging/resizing to the handle and resize zones
 * themselves.
 */
export const Widget = ({
  id,
  title,
  titleSymbol,
  children,
  headerAccessory,
  accentColor,
  groupLinePosition = "top",
  onRemove,
  onReset,
}: WidgetProps) => {
  const [headerEndSlot, setHeaderEndSlot] = useState<HTMLDivElement | null>(
    null,
  );
  const [titleEndSlot, setTitleEndSlot] = useState<HTMLDivElement | null>(null);
  const [overflowMenuItems, setOverflowMenuItems] = useState<ReactNode>(null);

  return (
    <WidgetHeaderEndSlotContext.Provider value={headerEndSlot}>
      <WidgetTitleEndSlotContext.Provider value={titleEndSlot}>
        <WidgetOverflowMenuItemsContext.Provider value={setOverflowMenuItems}>
          <Card size="1" className="workspace-widget">
            <Flex direction="column" height="100%" minHeight="0">
              <Flex
                className="workspace-widget__header"
                data-testid="widget-header-chrome"
                position="relative"
                flexShrink="0"
                align="center"
                justify="between"
                gap="2"
                px="2"
                py="1"
              >
                {accentColor && groupLinePosition !== "off" && (
                  <Box
                    className="workspace-widget__group-line"
                    data-testid="widget-group-line"
                    data-accent-color={accentColor}
                    data-line-position={groupLinePosition}
                    aria-hidden
                  />
                )}
                <Flex align="center" gap="2" minWidth="0" flexGrow="1">
                  <IconButton
                    type="button"
                    aria-label={`Drag widget ${title}`}
                    // Dragging is started by the grid engines' own mousedown /
                    // touchstart listeners on this element — there is no keyboard
                    // equivalent. Leaving it in the tab order would make it a
                    // no-op stop for keyboard users, so it is explicitly excluded.
                    tabIndex={-1}
                    variant="ghost"
                    color="gray"
                    size="1"
                    {...{ [WIDGET_DRAG_HANDLE_ATTR]: true }}
                  >
                    <DotsSixVerticalIcon
                      size={14}
                      aria-hidden
                      className="workspace-widget__drag-glyph"
                    />
                  </IconButton>
                  {headerAccessory && (
                    <Box flexShrink="0">{headerAccessory}</Box>
                  )}
                  <Flex
                    align="center"
                    gap="2"
                    minWidth="0"
                    className="workspace-widget__title"
                  >
                    <Text size="1" truncate color="gray" highContrast>
                      {title}
                    </Text>
                    {titleSymbol ? (
                      <>
                        <Text size="1" color="gray" highContrast>
                          ·
                        </Text>
                        {titleSymbol}
                      </>
                    ) : null}
                    <Flex
                      ref={setTitleEndSlot}
                      align="center"
                      flexShrink="0"
                      data-testid="widget-title-end"
                      className="workspace-widget__title-end"
                    />
                  </Flex>
                </Flex>
                <Flex align="center" gap="2" flexShrink="0">
                  <Flex
                    ref={setHeaderEndSlot}
                    align="center"
                    gap="2"
                    data-testid="widget-header-end"
                  />
                  {onRemove && (
                    <Box flexShrink="0">
                      <WidgetActionsMenu
                        widgetId={id}
                        onRemove={onRemove}
                        menuItems={overflowMenuItems}
                      />
                    </Box>
                  )}
                </Flex>
              </Flex>
              <Box
                className="workspace-widget__body"
                data-testid="widget-body"
                flexGrow="1"
                minHeight="0"
                overflow="hidden"
              >
                <Box
                  className="workspace-widget__body-content"
                  data-testid="widget-body-content"
                  height="100%"
                  minHeight="0"
                  overflow="hidden"
                >
                  <WidgetBodyErrorBoundary label={title} onReset={onReset}>
                    {children}
                  </WidgetBodyErrorBoundary>
                </Box>
              </Box>
            </Flex>
          </Card>
        </WidgetOverflowMenuItemsContext.Provider>
      </WidgetTitleEndSlotContext.Provider>
    </WidgetHeaderEndSlotContext.Provider>
  );
};
